import React from 'react';
import { StyleSheet } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { ScrollViewWithHeaderTitle, theme } from '@sg/garnish';

import { LoadingAnimation } from '@order/components';
import { TierName } from '@order/graphql';
import { useSweetpassTier } from '@order/hooks';
import { useTelemetry } from '@order/Telemetry';

import { useNavigateToMainTabs } from '../../navigation';
import type { SweetpassBenefitsScreenProps } from '../../navigation/AppNavigation.props';
import { useSweetpassBenefitsTelemetry } from './hooks';
import type {
  SweetpassBenefitsPageBenefitCtaHandler,
  SweetpassBenefitsPageCardCtaHandler,
  SweetpassBenefitsPageTag,
  SweetpassBenefitsPageUpgradeCtaHandler,
} from './SweetpassBenefitsScreen.loaded';
import { useBenefitsScreenLoadedContent } from './SweetpassBenefitsScreen.loaded';
import { SweetpassBenefitsScreenContent } from './SweetpassBenefitsScreenContent';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassBenefitsScreen = ({
  navigation,
}: SweetpassBenefitsScreenProps) => {
  const { isSweetpassEnabled, isSweetpassTierLoading, sweetpassTierName } =
    useSweetpassTier('cache-and-network');
  const { track } = useTelemetry();

  // ─────────────────────────────────────────────────────────────────

  const benefitsPageTag = getBenefitsPageTagForTier(sweetpassTierName);

  const { content: loadedContent, isLoading: isContentLoading } =
    useBenefitsScreenLoadedContent(
      isSweetpassTierLoading ? undefined : benefitsPageTag ?? '',
    );

  const isLoading = isSweetpassTierLoading || isContentLoading;

  const hasContent = isSweetpassEnabled && Boolean(loadedContent);

  const shouldRedirectToHome = !isLoading && !hasContent;

  // ─────────────────────────────────────────────────────────────────

  useSweetpassBenefitsTelemetry({
    isLoading,
    hasContent,
    shouldRedirectToHome,
  });

  React.useLayoutEffect(() => {
    if (shouldRedirectToHome) {
      navigation.navigate('MainTabs', {
        screen: 'HomeTab',
        params: { screen: 'Home' },
      });
    }
  }, [navigation, shouldRedirectToHome]);

  const handleBenefitCta: SweetpassBenefitsPageBenefitCtaHandler =
    React.useCallback(
      (action) => {
        if (action === 'challenges-rewards') {
          navigation.navigate('MainTabs', {
            screen: 'LoyaltyTab',
            params: { screen: 'SweetpassHome' },
          });
        }
      },
      [navigation],
    );

  const handleUpgradeCta: SweetpassBenefitsPageUpgradeCtaHandler =
    React.useCallback(
      async (action) => {
        if (action === 'upgrade_to_sweetpass_plus') {
          track('sweetpass_benefits.upgrade_cta.tapped');
          navigation.navigate('Modal', { screen: 'SweetpassUpgrade' });
        }
      },
      [navigation, track],
    );

  const navigateToMainTabs = useNavigateToMainTabs(navigation);

  const handleOnFaq = React.useCallback(() => {
    track('sweetpass_benefits.faq_cta.tapped');
    void WebBrowser.openBrowserAsync(
      'https://www.sweetgreen.com/sweetpass/faq',
      { windowFeatures: { target: '_blank' } },
    );
  }, [track]);

  const handleOnSweetpassAccount = React.useCallback(() => {
    track('sweetpass_benefits.membership_cta.tapped');
    void navigateToMainTabs('AccountTab', { screen: 'SweetpassMembership' });
  }, [navigateToMainTabs, track]);

  const handleCardCta: SweetpassBenefitsPageCardCtaHandler = React.useCallback(
    (action: string) => {
      if (action === 'view-all') {
        handleOnFaq();
      }

      if (action === 'edit') {
        handleOnSweetpassAccount();
      }
    },
    [handleOnFaq, handleOnSweetpassAccount],
  );

  // ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <LoadingAnimation />;
  }

  if (!isSweetpassEnabled || !loadedContent || !sweetpassTierName) {
    return null;
  }

  return (
    <ScrollViewWithHeaderTitle
      testID="sweetpass-benefits.root.container"
      style={styles.scrollContainer}
      contentContainerStyle={styles.scrollContent}
    >
      <SweetpassBenefitsScreenContent
        testID="sweetpass-benefits-screen"
        content={loadedContent}
        handleUpgradeCta={handleUpgradeCta}
        handleBenefitCta={handleBenefitCta}
        handleCardCta={handleCardCta}
      />
    </ScrollViewWithHeaderTitle>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
    backgroundColor: theme.colors.KHAKI_3,
  },
  scrollContent: {
    height: '100%',
  },
});

// ────────────────────────────────────────────────────────────────────────────────

const getBenefitsPageTagForTier = (
  tierName?: TierName,
): SweetpassBenefitsPageTag | undefined => {
  if (tierName === TierName.Sweetpass) {
    return 'sweetpass-benefits-page';
  }

  if (tierName === TierName.SweetpassPlus) {
    return 'sweetpass-plus-benefits-page';
  }

  return undefined;
};
